import { useEffect, useState } from 'react';
import {
  Target, CheckSquare, AlertTriangle, Megaphone,
  TrendingUp, CalendarDays, Users, Network, Clock, MonitorPlay,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DateInput } from '@/components/ui/DateInput';
import { LeadReports } from '@/components/reports/LeadReports';
import { TaskReports } from '@/components/reports/TaskReports';
import { CampaignReports } from '@/components/reports/CampaignReports';
import { TeamPerformance } from '@/components/reports/TeamPerformance';
import { ResponseTimeReport } from '@/components/reports/ResponseTimeReport';
import { MeetingPunctualityReport } from '@/components/reports/MeetingPunctualityReport';
import api from '@/lib/api';

type Tab = 'leads' | 'tasks' | 'campaigns' | 'team' | 'response' | 'meetings';
type Granularity = 'day' | 'week' | 'month';

const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'leads', label: 'Leads', icon: Users },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { key: 'team', label: 'Team', icon: Network },
  { key: 'response', label: 'Response Time', icon: Clock },
  { key: 'meetings', label: 'Meetings', icon: MonitorPlay },
];

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

interface Overview {
  totalLeads: number;
  convertedLeads: number;
  conversionRate: number;
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  activeCampaigns: number;
}

export function ReportsPage() {
  const defaults = defaultDateRange();
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [tab, setTab] = useState<Tab>('leads');
  const [overview, setOverview] = useState<Overview | null>(null);

  useEffect(() => {
    api
      .get('/reports/overview', { params: { dateFrom, dateTo } })
      .then(r => setOverview(r.data))
      .catch(() => {});
  }, [dateFrom, dateTo]);

  const kpis = overview
    ? [
        {
          label: 'Total Leads',
          value: overview.totalLeads,
          subtitle: `${overview.convertedLeads} converted`,
          icon: <Target className="h-5 w-5 text-[#6366F1]" />,
          iconBg: 'bg-indigo-100',
        },
        {
          label: 'Conversion Rate',
          value: `${overview.conversionRate}%`,
          subtitle: `${overview.convertedLeads} of ${overview.totalLeads}`,
          icon: <TrendingUp className="h-5 w-5 text-emerald-500" />,
          iconBg: 'bg-emerald-100',
        },
        {
          label: 'Tasks',
          value: `${overview.completedTasks}/${overview.totalTasks}`,
          subtitle: `${overview.overdueTasks} overdue`,
          icon: <CheckSquare className="h-5 w-5 text-amber-500" />,
          iconBg: 'bg-amber-100',
          alert: overview.overdueTasks > 0,
        },
        {
          label: 'Active Campaigns',
          value: overview.activeCampaigns,
          subtitle: 'Running now',
          icon: <Megaphone className="h-5 w-5 text-[#6366F1]" />,
          iconBg: 'bg-indigo-100',
        },
      ]
    : [];

  return (
    <DashboardLayout title="Reports">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-[#6366F1]">Reports</h2>
          <p className="text-sm text-[#64748B] mt-1">Analytics and insights for your business</p>
        </div>

        {/* Date Filter */}
        <div className="bg-white rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-4">
          <div>
            <span className="block text-xs font-medium text-slate-500 mb-1.5">
              <CalendarDays className="h-3.5 w-3.5 inline mr-1" />
              From
            </span>
            <DateInput value={dateFrom} onChange={setDateFrom} className="w-40" inputClassName="h-9" />
          </div>
          <div>
            <span className="block text-xs font-medium text-slate-500 mb-1.5">To</span>
            <DateInput value={dateTo} onChange={setDateTo} className="w-40" inputClassName="h-9" />
          </div>
          <div>
            <span className="block text-xs font-medium text-slate-500 mb-1.5">Granularity</span>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden h-9">
              {(['day', 'week', 'month'] as Granularity[]).map(g => (
                <button
                  key={g}
                  onClick={() => setGranularity(g)}
                  className={`px-3 text-xs font-medium capitalize transition-colors ${
                    granularity === g
                      ? 'bg-[#6366F1] text-white'
                      : 'bg-white text-slate-600 hover:bg-gray-50'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        {overview && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {kpis.map(card => (
              <div key={card.label} className="bg-white rounded-xl p-5 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${card.iconBg}`}>
                    {card.icon}
                  </div>
                  {(card as any).alert && (
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                  )}
                </div>
                <div className="text-2xl font-bold text-[#1E293B]">{card.value}</div>
                <div className="text-sm font-medium text-[#64748B] mt-0.5">{card.label}</div>
                <div className="text-xs text-[#94A3B8] mt-0.5">{card.subtitle}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs + their content in ONE card — tab bar on top, report below */}
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="flex overflow-x-auto border-b border-gray-100 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              const count =
                t.key === 'leads' ? overview?.totalLeads
                  : t.key === 'tasks' ? overview?.totalTasks
                    : t.key === 'campaigns' ? overview?.activeCampaigns
                      : undefined;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`-mb-px flex flex-shrink-0 items-center gap-2 border-b-2 px-5 py-4 text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors focus:outline-none focus-visible:bg-indigo-50/60 ${
                    active
                      ? 'border-[#6366F1] text-[#6366F1]'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                  {count !== undefined && (
                    <span className={`min-w-[22px] rounded-full px-2 py-0.5 text-center text-[11px] font-semibold tracking-normal ${
                      active ? 'bg-[#6366F1] text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content — same card, light panel so inner cards/tables stand out */}
          <div className="bg-slate-50/50 p-4 sm:p-5">
            {tab === 'leads' && (
              <LeadReports dateFrom={dateFrom} dateTo={dateTo} granularity={granularity} />
            )}
            {tab === 'tasks' && (
              <TaskReports dateFrom={dateFrom} dateTo={dateTo} />
            )}
            {tab === 'campaigns' && (
              <CampaignReports dateFrom={dateFrom} dateTo={dateTo} />
            )}
            {tab === 'team' && (
              <TeamPerformance dateFrom={dateFrom} dateTo={dateTo} />
            )}
            {tab === 'response' && (
              <ResponseTimeReport dateFrom={dateFrom} dateTo={dateTo} />
            )}
            {tab === 'meetings' && (
              <MeetingPunctualityReport dateFrom={dateFrom} dateTo={dateTo} />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
